import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Palette, Upload, Lock, ImageIcon } from "lucide-react";

const MAX_LOGO_BYTES = 500 * 1024;

export const ProviderBranding = () => {
  const [cfg, setCfg] = useState(null);
  const [logo, setLogo] = useState(null);
  const [accent, setAccent] = useState("#064E3B");
  const [fromName, setFromName] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoChanged, setLogoChanged] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/provider/branding").then((r) => {
    setCfg(r.data);
    setLogo(r.data.logo_url || null);
    setAccent(r.data.branding?.accent_color || "#064E3B");
    setFromName(r.data.branding?.email_from_name || "");
    setLogoChanged(false);
  });
  useEffect(() => { load(); }, []);

  if (!cfg) return null;

  const caps = cfg.caps;
  const locked = !cfg.white_label_enabled;

  const onLogo = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > MAX_LOGO_BYTES) { toast.error("Logo must be under 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setLogo(reader.result); setLogoChanged(true); };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (caps.logo && logoChanged) payload.logo = logo || "";
      if (caps.accent_color) payload.accent_color = accent;
      if (caps.email_from_name) payload.email_from_name = fromName;
      await api.put("/provider/branding", payload);
      toast.success("Branding saved · applied to client emails & dashboards");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="branding-editor" className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
      <div className="flex items-center gap-2">
        <Palette className="w-5 h-5 text-emerald-700" />
        <h2 className="font-serif text-2xl text-emerald-950">White-label branding</h2>
      </div>

      {locked ? (
        <div data-testid="branding-locked" className="mt-4 flex items-start gap-3 rounded-xl border border-dashed border-stone-300 bg-stone-50 p-5">
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-stone-400" />
          <div>
            <p className="text-sm text-stone-700">White-labeling is available on the <b>Crew Enterprise</b> plan.</p>
            <p className="mt-1 text-xs text-stone-500">Upgrade to put your own logo, colors, and email sender in front of your clients.</p>
            <a href="/pricing" data-testid="branding-upgrade" className="mt-3 inline-block rounded-full bg-emerald-900 px-4 py-2 text-xs text-white hover:bg-emerald-800">View plans</a>
          </div>
        </div>
      ) : (
        <div className="mt-5 grid gap-6 sm:grid-cols-2">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Company logo</Label>
            <div className="mt-2 flex items-center gap-3">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
                {logo ? <img src={logo} alt="logo" className="max-h-full max-w-full object-contain" /> : <ImageIcon className="h-6 w-6 text-stone-300" />}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="branding-logo-input" />
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()}>
                  <Upload className="mr-1 h-4 w-4" /> Upload
                </Button>
                {logo && <button data-testid="branding-logo-remove" onClick={() => { setLogo(null); setLogoChanged(true); }} className="text-xs text-stone-400 underline hover:text-red-600">remove</button>}
              </div>
            </div>
            <p className="mt-2 text-xs text-stone-400">PNG/SVG under 500KB. Shows on client emails & dashboards.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Accent color</Label>
            <div className="mt-2 flex items-center gap-3">
              <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} data-testid="branding-color-input" className="h-11 w-14 cursor-pointer rounded-lg border border-stone-200 bg-white p-1" />
              <Input value={accent} onChange={(e) => setAccent(e.target.value)} className="rounded-xl h-11 w-32" data-testid="branding-color-hex" />
              <div className="h-11 flex-1 rounded-xl" style={{ backgroundColor: accent }} />
            </div>
          </div>

          <div className="sm:col-span-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Email sender name</Label>
              {!caps.email_from_name && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] text-stone-500">Crew Enterprise only</span>}
            </div>
            <Input
              value={caps.email_from_name ? fromName : cfg.company || "PlantFlow"}
              onChange={(e) => setFromName(e.target.value)}
              disabled={!caps.email_from_name}
              placeholder={cfg.company || "Your company"}
              className="mt-2 rounded-xl h-11 disabled:opacity-60"
              data-testid="branding-fromname-input"
            />
            <p className="mt-1.5 text-xs text-stone-400">The "from" name your clients see on automated milestone emails.</p>
          </div>

          <div className="sm:col-span-2">
            <Button data-testid="branding-save" onClick={save} disabled={busy} className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">
              {busy ? "Saving…" : "Save branding"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
